import { Cache } from 'memory-cache';
import { PriorityTaskQueue } from 'owa-task-queue';
import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import type { TokenRequestParams } from 'owa-tokenprovider-utils';
import type { TokenCallbackType } from '../schema/TokenCallbackType';
import type { ITokenQueue } from './ITokenQueue';
import {
    MAX_NUM_OF_GET_ACCESS_TOKEN_REQUESTS,
    DELAY_FOR_GET_ACCESS_TOKEN_REQUESTS,
    TIMEOUT_FOR_TOKEN_REQUEST_PROMISE,
} from '../const/constants';
import type { LogStartPerformanceUsage } from 'owa-analytics-start';
import { isBootFeatureEnabled } from 'owa-metatags';

export class TokenQueue implements ITokenQueue {
    private requestPromiseCache: Cache<string, Promise<TokenResponse>>;
    private tokenQueue: PriorityTaskQueue<TokenResponse>;

    constructor() {
        this.tokenQueue = new PriorityTaskQueue<TokenResponse>({
            maxParallelTasks: MAX_NUM_OF_GET_ACCESS_TOKEN_REQUESTS,
            taskDelay: DELAY_FOR_GET_ACCESS_TOKEN_REQUESTS,
            isUserBlocking: isBootFeatureEnabled('perf-prioritize-tokenFetch'),
        });
        this.requestPromiseCache = new Cache();
    }

    /**
     * This provides caching to store promise to token request mapping (both business and consumer scenarios).
     * Used when similar requests are made for the same cacheKey, this cache
     * is looked up to return an existing promise to the caller.
     */
    public enqueueRequestIfNotCached(
        cacheKey: string,
        authParams: TokenRequestParams,
        authTaskCallback: TokenCallbackType,
        perfLog: LogStartPerformanceUsage
    ): Promise<TokenResponse> {
        const cachedRequestPromise = this.getCachedPromise(cacheKey);

        if (cachedRequestPromise) {
            perfLog.addCheckpoint('CachedPromise');
            return cachedRequestPromise;
        } else {
            const requestPromise: Promise<TokenResponse> = this.tokenQueue.add(() =>
                authTaskCallback(authParams, perfLog)
            );
            this.putPromiseInCache(cacheKey, requestPromise);
            perfLog.addCheckpoint('PromiseAddedToAuthCache');
            return requestPromise;
        }
    }

    /**
     * Gets the cached promise for the "already-seen" cacheKey.
     */
    public getCachedPromise(cacheKey: string): Promise<TokenResponse> | null {
        return this.requestPromiseCache.get(cacheKey);
    }

    /**
     * Deletes the cached promise for the "already-seen" cacheKey, when the Promise has been resolved
     */
    public deleteCachedPromise(cacheKey: string): boolean {
        return this.requestPromiseCache.del(cacheKey);
    }

    /**
     * Puts the duplicate request to the PromiseCache
     */
    public putPromiseInCache(cacheKey: string, promise: Promise<TokenResponse>): void {
        this.requestPromiseCache.put(cacheKey, promise, TIMEOUT_FOR_TOKEN_REQUEST_PROMISE);
    }
}
